import React, { useEffect, useState, useRef } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}
export function MultiSelect({
  label,
  options,
  selected,
  onChange
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);
  const toggle = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };
  const isAll = selected.length === 0;
  const displayText = isAll ? `${label}: All` : selected.length === 1 ? `${label}: ${selected[0]}` : `${label}: ${selected.length} selected`;
  return <div ref={ref} className="relative flex-shrink-0" data-id="element-311">
      <button type="button" onClick={() => setOpen(v => !v)} className={`group flex items-center gap-1.5 text-xs font-medium rounded-full pl-3 pr-2 py-1.5 border transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-cobalt-500 ${isAll ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700' : 'bg-cobalt-50 dark:bg-cobalt-900/30 border-cobalt-200 dark:border-cobalt-800 text-cobalt-700 dark:text-cobalt-300 hover:bg-cobalt-100 dark:hover:bg-cobalt-900/50'}`} data-id="element-312">
        <span className="whitespace-nowrap" data-id="element-313">{displayText}</span>
        {!isAll && <span onClick={clear} role="button" aria-label={`Clear ${label} filter`} className="p-0.5 rounded-full hover:bg-cobalt-200/70 dark:hover:bg-cobalt-800 transition-colors" data-id="element-314">
            <X className="w-3 h-3" data-id="element-315" />
          </span>}
        <ChevronDown className={`w-3 h-3 text-current opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-316" />
      </button>

      {open && <div className="absolute left-0 top-full mt-1.5 z-40 w-60 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150" data-id="element-317">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-700" data-id="element-318">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400" data-id="element-319">
              {label}
            </span>
            <button onClick={() => onChange([])} disabled={isAll} className="text-[11px] font-medium text-cobalt-600 dark:text-cobalt-400 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed" data-id="element-320">
              Clear
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto py-1" data-id="element-321">
            {options.length === 0 && <div className="px-3 py-2 text-xs text-slate-400 italic" data-id="element-322">
                No options
              </div>}
            {options.map(opt => {
          const checked = selected.includes(opt);
          return <button key={opt} type="button" onClick={() => toggle(opt)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors text-left" data-id="element-323">
                  <span className={`flex items-center justify-center w-4 h-4 rounded border transition-colors ${checked ? 'bg-cobalt-600 border-cobalt-600 text-white' : 'border-slate-300 dark:border-slate-600 text-transparent'}`} data-id="element-324">
                    <Check className="w-3 h-3" strokeWidth={3} data-id="element-325" />
                  </span>
                  <span className="truncate" data-id="element-326">{opt}</span>
                </button>;
        })}
          </div>
        </div>}
    </div>;
}